import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function CertificatePicker({
  certificates,
  selectedCertIds,
  onToggleCert,
  onSelectAll,
  onDeselectAll,
  loading,
  searchFilter,
  onSearchFilterChange,
  customIdValue,
  onCustomIdChange,
  onAddCustomId,
  emptyMessage,
  getCertNameById,
  maxHeight = 260,
}) {
  const filtered = certificates.filter((c) =>
    searchFilter ? c.name?.toLowerCase().includes(searchFilter.toLowerCase()) : true
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search certificates..."
          value={searchFilter}
          onChange={(e) => onSearchFilterChange(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={onSelectAll}
          disabled={certificates.length === 0}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={onDeselectAll}
          disabled={selectedCertIds.length === 0}
        >
          Clear
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : certificates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage || 'No certificates available.'}
        </Typography>
      ) : (
        <Box
          sx={{
            maxHeight,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
          }}
        >
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              No certificates match your search.
            </Typography>
          ) : (
            filtered.map((cert) => (
              <FormControlLabel
                key={cert.productCatalogueId}
                sx={{
                  display: 'flex',
                  width: '100%',
                  mx: 0,
                  '&:hover': { bgcolor: 'action.hover' },
                  borderRadius: 0.5,
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedCertIds.includes(cert.productCatalogueId)}
                    onChange={() => onToggleCert(cert.productCatalogueId)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{cert.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {cert.productCatalogueId}
                    </Typography>
                  </Box>
                }
              />
            ))
          )}
        </Box>
      )}

      {onAddCustomId && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
          <TextField
            size="small"
            placeholder="Custom catalogue ID"
            value={customIdValue || ''}
            onChange={(e) => onCustomIdChange(e.target.value)}
            type="number"
            sx={{ flexGrow: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddCustomId();
              }
            }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={onAddCustomId}
            disabled={!customIdValue}
          >
            Add
          </Button>
        </Box>
      )}

      {selectedCertIds.length > 0 && getCertNameById && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {selectedCertIds.length} selected:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedCertIds.map((id) => {
              const name = getCertNameById(id);
              const isKnown = !name.startsWith('Unknown');
              return (
                <Chip
                  key={id}
                  label={isKnown ? name : `ID: ${id}`}
                  size="small"
                  color={isKnown ? 'primary' : 'default'}
                  variant={isKnown ? 'filled' : 'outlined'}
                  onDelete={() => onToggleCert(id)}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
